import type { CSSProperties, ReactNode } from "react";
import InnerHTML from "dangerously-set-html-content";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { HtmlVideoPlayer } from "@circle-react-shared/uikit/VideoPlayer/HtmlVideoPlayer";
import { HlsHtmlVideoPlayer } from "./HlsHtmlVideoPlayer";
import { isHlsSupported } from "./utils";

export interface CircleVideoPlayerProps {
  autoPlay?: boolean;
  className?: string;
  contentType?: string;
  description?: string;
  html?: string;
  isLooped?: boolean;
  isMuted?: boolean;
  isPreviewMode?: boolean;
  placeholder?: ReactNode;
  poster?: string;
  preload?: "none" | "metadata" | "auto";
  shouldAutoPlay?: boolean;
  styles?: CSSProperties;
  url?: string;
}

export const CircleVideoPlayer = ({
  url = "",
  contentType = "video/mp4",
  html = "",
  isPreviewMode = false,
  preload = "auto",
  poster = "",
  placeholder = null,
  className = "",
  isMuted = false,
  isLooped = false,
  description = "",
  shouldAutoPlay = false,
  styles,
}: CircleVideoPlayerProps) => {
  const isHls = isHlsSupported(url);

  const optionsProps = {
    preload,
    poster,
    muted: isMuted,
    loop: isLooped,
    description,
    className: classNames("wb-w-full wb-bg-black wb-object-cover", className),
  };

  if (html) {
    return (
      <InnerHTML
        className={classNames(
          "wb-flex-1 wb-[&>iframe]:wb-h-auto wb-[&>iframe]:wb-max-w-full",
          className,
          {
            "wb-pointer-events-none": isPreviewMode,
          },
        )}
        html={html}
        style={styles}
      />
    );
  }

  if (isHls) {
    return (
      <HlsHtmlVideoPlayer
        {...optionsProps}
        playbackUrl={url}
        styles={styles}
        shouldAutoPlay={shouldAutoPlay}
      />
    );
  }

  if (url) {
    return (
      <HtmlVideoPlayer
        {...optionsProps}
        src={url}
        type={contentType}
        styles={styles}
        shouldAutoPlay={shouldAutoPlay}
      />
    );
  }

  if (placeholder) {
    return <div style={styles}>{placeholder}</div>;
  }

  return null;
};
